import React, { useState } from 'react';
import {
	CircularProgress,
	IconButton,
	Link,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import { showToast } from '@/features/toast/toastSlice';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useGetEngageReportsQuery, useUpdateEngageReportMutation } from '@/features/deals/dealsApi';
import { FolderSelectDialog } from '@/components/dialogs/FolderSelectDialog';
import { useMsGraph } from '@/hooks/useMsGraph';
import { useBannerColours } from '@/hooks/useBannerColours';
import {
	ENGAGE_REPORT_STATUSES,
	ENGAGE_REPORT_TYPES,
	isSharepointOnlyFailure,
} from '@/utils/engageReports';

const formatFailureSummary = (reports) => {
	const names = [...new Set(reports.map((r) => ENGAGE_REPORT_TYPES[r.reportType].name))];
	return `${reports.length} generated ${names.join(' and ')} report${
		reports.length === 1 ? '' : 's'
	} failed to save to Sharepoint.`;
};

/**
 * Folio Engage reports on this deal that were generated but whose Word copy failed to
 * save to SharePoint, with a link to save them again from their Firebase copy, as
 * DebtorRemindersBanner does for debtor reminders.
 *
 * @param {Object} props
 * @param {string[]} props.reportTypes - The ENGAGE_REPORT_TYPES keys this tab generates.
 * @returns {JSX.Element|null}
 */
const EngageReportsBanner = ({ reportTypes }) => {
	const dispatch = useDispatch();
	const { dealId, dealRow } = useObjectContext();
	const { uploadFileAndRetry } = useMsGraph();
	const theme = useTheme();
	const { backgroundColor, border, textColor } = useBannerColours('error');

	const [folderDialogOpen, setFolderDialogOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isDismissing, setIsDismissing] = useState(false);

	const { data: engageReports } = useGetEngageReportsQuery(dealId, { skip: !dealId });

	const [updateEngageReport] = useUpdateEngageReportMutation();

	const failedReports = (engageReports ?? []).filter(
		(report) => reportTypes.includes(report.reportType) && isSharepointOnlyFailure(report),
	);

	if (!dealId || failedReports.length === 0) return null;

	const isBusy = isSaving || isDismissing;

	const handleFolderConfirmed = async ({ selectedFolder, selectedDrive }) => {
		setFolderDialogOpen(false);
		setIsSaving(true);

		let successCount = 0;
		let failureCount = 0;

		for (const report of failedReports) {
			try {
				// Only the files that didn't reach SharePoint the first time
				for (const artifact of report.artifacts.filter((a) => !a.sharepointFileId)) {
					const response = await fetch(artifact.downloadUrl);
					const blob = await response.blob();
					const file = new File([blob], artifact.fileName, {
						type: blob.type || artifact.mimeType,
					});

					const uploaded = await uploadFileAndRetry({
						driveId: selectedDrive?.parentReference?.driveId,
						itemId: selectedFolder,
						file,
						silent: true,
					});

					if (!uploaded) {
						throw new Error('Upload to Sharepoint failed');
					}
				}

				await updateEngageReport({
					dealId,
					docId: report.id,
					status: ENGAGE_REPORT_STATUSES.complete,
				}).unwrap();

				successCount += 1;
			} catch (e) {
				console.error('[EngageReportsBanner] Failed to save report file to Sharepoint', e);
				failureCount += 1;
			}
		}

		setIsSaving(false);

		if (failureCount === 0) {
			dispatch(
				showToast({
					message: `Saved ${successCount} report${successCount === 1 ? '' : 's'} to Sharepoint`,
					success: true,
				}),
			);
		} else {
			dispatch(
				showToast({
					message: `Saved ${successCount}, failed to save ${failureCount} report${
						failureCount === 1 ? '' : 's'
					} to SharePoint`,
					error: true,
				}),
			);
		}
	};

	const handleDismiss = async () => {
		setIsDismissing(true);
		try {
			await Promise.all(
				failedReports.map((report) =>
					updateEngageReport({
						dealId,
						docId: report.id,
						status: ENGAGE_REPORT_STATUSES.dismissed,
					}).unwrap(),
				),
			);
		} catch (e) {
			console.error('[EngageReportsBanner] Failed to dismiss reports', e);
			dispatch(
				showToast({
					message: 'Failed to dismiss report notice: please try again',
					error: true,
				}),
			);
		} finally {
			setIsDismissing(false);
		}
	};

	return (
		<Stack
			direction='row'
			spacing={1}
			sx={{
				alignItems: 'center',
				justifyContent: 'space-between',
				px: 1,
				borderRadius: '4px',
				backgroundColor,
				border,
			}}
		>
			<Typography variant='task_updated' sx={{ color: textColor }}>
				{formatFailureSummary(failedReports)}{' '}
				{isSaving ? (
					'Saving...'
				) : (
					<Link
						component='button'
						type='button'
						aria-label='Save report files to Sharepoint'
						disabled={isBusy}
						onClick={() => setFolderDialogOpen(true)}
						sx={{
							color: theme.palette.primary.main,
							font: 'inherit',
							verticalAlign: 'baseline',
						}}
					>
						Click here
					</Link>
				)}{' '}
				to try save again.
			</Typography>

			<Stack direction='row' spacing={0.5} sx={{ alignItems: 'center' }}>
				<Tooltip title='Dismiss'>
					<span>
						<IconButton
							size='small'
							aria-label='Dismiss report notice'
							disabled={isBusy}
							onClick={handleDismiss}
						>
							{isDismissing ? (
								<CircularProgress size='16px' />
							) : (
								<CloseIcon sx={{ fontSize: '18px' }} />
							)}
						</IconButton>
					</span>
				</Tooltip>
			</Stack>

			<Formik
				initialValues={{
					deal: dealRow,
					selectedFolder: null,
					selectedDrive: null,
					folderPath: '',
				}}
				onSubmit={() => {}}
			>
				<FolderSelectDialog
					initialItemId={dealRow?.driveId}
					open={folderDialogOpen}
					handleClose={() => setFolderDialogOpen(false)}
					onConfirm={handleFolderConfirmed}
				/>
			</Formik>
		</Stack>
	);
};
EngageReportsBanner.displayName = 'EngageReportsBanner';
export default EngageReportsBanner;
